import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './Factures.css';

export default function ModifierDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [originalStatut, setOriginalStatut] = useState('');
    
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const [devis, setDevis] = useState({
        id_client: '',
        date_proforma: '',
        date_validite: '',
        remise_globale: 0,
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    useEffect(() => {
        fetchDevis();
        fetchClients();
        fetchProduits();
        fetchTaxes();
    }, [id]);

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data);
            setOriginalStatut(result.data.statut);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

const fetchProduits = async () => {
    try {
        const token = localStorage.getItem("token");
        const response = await fetch(
            `${API_BASE_URL}/produits.php`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
            const result = await response.json();
            // Les données contiennent déjà stock_reel et quantite_disponible
            const produitsAvecStock = (result.data || []).map(p => ({
                id_produit: p.id_produit,
                reference: p.reference,
                designation: p.designation,
                prix_vente_ht: parseFloat(p.prix_vente_ht),
                stock_actuel: p.quantite_disponible || p.stock_reel || 0,  // Utiliser quantite_disponible
                seuil_alerte: p.seuil_alerte || 5,
                tva_taux: parseFloat(p.tva_taux) || 18,
                css_taux: parseFloat(p.css_taux) || 1,
                description: p.description || '',
                unite_mesure: p.unite_mesure || ''
            }));
            setProduits(produitsAvecStock);
            console.log("Produits chargés:", produitsAvecStock.length);
        } else {
            console.error("Erreur API produits:", response.status);
        }
    } catch (err) {
        console.error("Erreur chargement produits", err);
        setError("Impossible de charger les produits");
    }
};

   const ajouterLigne = (produit) => {
    const nouvelleLigne = {
        id_produit: produit.id_produit,
        reference: produit.reference,
        designation: produit.designation,
        quantite: 1,
        prix_unitaire_ht: produit.prix_vente_ht,
        tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
        css_taux: taxes.css_active ? taxes.css_taux : 0,
        remise: 0,
        stock_actuel: produit.stock_actuel || 0  // 👈 AJOUTER CETTE LIGNE
    };
    setDevis({
        ...devis,
        lignes: [...devis.lignes, nouvelleLigne]
    });
    setShowProduitModal(false);
};

    const supprimerLigne = (index) => {
        const nouvellesLignes = devis.lignes.filter((_, i) => i !== index);
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...devis.lignes];
        nouvellesLignes[index][champ] = valeur;
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        devis.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            
            if (taxes.tva_active) {
                total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            }
            if (taxes.css_active) {
                total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
            }
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (devis.remise_globale || 0);

        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    const isEditable = () => {
        return devis.statut === 'brouillon';
    };

    const canChangeStatus = () => {
        return originalStatut === 'envoye' && ['accepte', 'refuse'].includes(devis.statut);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!devis.id_client) {
            setError("Veuillez sélectionner un client");
            return;
        }
        
        if (devis.lignes.length === 0) {
            setError("Veuillez ajouter au moins un produit");
            return;
        }

        if (originalStatut === 'envoye' && !canChangeStatus()) {
            setError("Vous ne pouvez modifier que le statut (Accepté/Refusé) pour ce devis");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(devis)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Devis mis à jour avec succès !");
                setTimeout(() => {
                    navigate('/ventes/devisListe');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccess("Devis supprimé avec succès !");
                setTimeout(() => {
                    navigate('/ventes/devisListe');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoye: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyé' },
            accepte: { color: '#10b981', icon: 'ri-check-double-line', label: 'Accepté' },
            refuse: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Refusé' },
            converti_facture: { color: '#21ACCD', icon: 'ri-file-list-3-line', label: 'Converti' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge" style={{ 
                background: `${config.color}`, 
                color: 'white',
                padding: '0.4rem 1rem',
                borderRadius: '30px',
                fontSize: '0.75rem',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
            }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du devis...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-pencil-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Modifier le devis</h4>
                                    <p className="text-muted small mb-0">
                                        {devis.numero_proforma}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/ventes/devisListe')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-white">
                                                <i className="ri-information-line me-2"></i>
                                                Informations générales
                                            </h5>
                                            <div>
                                                {getStatutBadge(devis.statut)}
                                            </div>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Client <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        value={devis.id_client}
                                                        onChange={(e) => setDevis({...devis, id_client: e.target.value})}
                                                        disabled={!isEditable()}
                                                        required
                                                        className="border-2 rounded-3"
                                                    >
                                                        <option value="">Sélectionner un client</option>
                                                        {clients.map(client => (
                                                            <option key={client.id_client} value={client.id_client}>
                                                                {client.raison_sociale} ({client.code_client})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {!isEditable() && (
                                                        <Form.Text className="text-muted small">
                                                            Le client ne peut pas être modifié pour ce devis
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date devis</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={devis.date_proforma}
                                                        onChange={(e) => setDevis({...devis, date_proforma: e.target.value})}
                                                        disabled={!isEditable()}
                                                        required
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date validité</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={devis.date_validite}
                                                        onChange={(e) => setDevis({...devis, date_validite: e.target.value})}
                                                        disabled={!isEditable()}
                                                        required
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Statut</Form.Label>
                                                    <Form.Select
                                                        value={devis.statut}
                                                        onChange={(e) => setDevis({...devis, statut: e.target.value})}
                                                        className="border-2 rounded-3"
                                                    >
                                                        <option value="brouillon">Brouillon</option>
                                                        <option value="envoye">Envoyé</option>
                                                        <option value="accepte">Accepté</option>
                                                        <option value="refuse">Refusé</option>
                                                        <option value="converti_facture">Converti en facture</option>
                                                    </Form.Select>
                                                    {originalStatut === 'envoye' && (
                                                        <Form.Text className="text-info small">
                                                            Vous pouvez changer le statut en Accepté ou Refusé
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={devis.notes || ''}
                                                        onChange={(e) => setDevis({...devis, notes: e.target.value})}
                                                        disabled={!isEditable()}
                                                        placeholder="Conditions particulières, délais, etc..."
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes de devis */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            disabled={!isEditable()}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {devis.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 products-table">
                                                    <thead className="bg-light">
                                                        <tr>
                                                             <th style={{ width: '30%' }}>Désignation</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix HT</th>
                                                            <th className="text-center">Remise</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center">TVA</th>
                                                            <th className="text-center pe-4"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {devis.lignes.map((ligne, index) => (
                                                            <tr key={index} className="product-row">
                                                               
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                             <td className="text-center" style={{ width: '120px' }}>
    <Form.Control
        type="number"
        min="1"
        max={ligne.stock_actuel || 999999}
        value={ligne.quantite}
        onChange={(e) => {
            let nouvelleQuantite = parseInt(e.target.value) || 1;
            const stockDisponible = ligne.stock_actuel;
            
            // Si le stock est défini et la quantité dépasse
            if (stockDisponible !== undefined && nouvelleQuantite > stockDisponible) {
                nouvelleQuantite = stockDisponible;
                // Optionnel : afficher une notification
                setError(`Quantité limitée à ${stockDisponible} (stock disponible)`);
                setTimeout(() => setError(""), 3000);
            }
            updateLigne(index, 'quantite', nouvelleQuantite);
        }}
        size="sm"
        disabled={!isEditable()}
        className={`text-center ${ligne.stock_actuel !== undefined && ligne.quantite > ligne.stock_actuel ? 'is-invalid' : ''}`}
    />
    {ligne.stock_actuel !== undefined && (
        <div className="mt-1">
            <small className={`${ligne.quantite > ligne.stock_actuel ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '0.65rem' }}>
                <i className="ri-stack-line me-1"></i>
                Stock: {ligne.stock_actuel}
            </small>
            {ligne.quantite > ligne.stock_actuel && (
                <small className="text-danger d-block" style={{ fontSize: '0.6rem' }}>
                    <i className="ri-error-warning-line me-1"></i>
                    Quantité supérieure au stock
                </small>
            )}
        </div>
    )}
</td>
                                                                <td className="text-end" style={{ width: '120px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={ligne.prix_unitaire_ht}
                                                                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        disabled={isEditable()}
                                                                        className="text-end"
                                                                    />
                                                                </td>
                                                                <td className="text-center" style={{ width: '100px' }}>
                                                                    <InputGroup size="sm">
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            value={ligne.remise || 0}
                                                                            onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                            disabled={!isEditable()}
                                                                            className="text-center"
                                                                        />
                                                                        <InputGroup.Text>%</InputGroup.Text>
                                                                    </InputGroup>
                                                                </td>
                                                                <td className="text-end fw-bold text-primary">
                                                                    {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                </td>
                                                                <td className="text-center text-warning">
                                                                    {taxes.tva_active ? (ligne.tva_taux || taxes.tva_taux) : 0}%
                                                                </td>
                                                                <td className="text-center pe-4">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                        disabled={!isEditable()}
                                                                    >
                                                                        <i className="ri-delete-bin-line fs-16"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                 
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={() => setShowProduitModal(true)}
                                                    disabled={!isEditable()}
                                                    className="rounded-pill px-4"
                                                    style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line d-flex justify-content-between mb-2">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        {taxes.tva_active && totaux.total_tva > 0 && (
                                            <div className="recap-line d-flex justify-content-between mb-2">
                                                <span className="text-muted">TVA ({taxes.tva_taux}%)</span>
                                                <span className="text-warning fw-semibold">{formatCurrency(totaux.total_tva)}</span>
                                            </div>
                                        )}
                                        {taxes.css_active && totaux.total_css > 0 && (
                                            <div className="recap-line d-flex justify-content-between mb-2">
                                                <span className="text-muted">CSS ({taxes.css_taux}%)</span>
                                                <span className="text-info fw-semibold">{formatCurrency(totaux.total_css)}</span>
                                            </div>
                                        )}
                                        <hr />
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold text-primary fs-5">{formatCurrency(totaux.total_ttc)}</span>
                                        </div>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Remise globale (FCFA)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={devis.remise_globale}
                                                onChange={(e) => setDevis({...devis, remise_globale: parseFloat(e.target.value) || 0})}
                                                disabled={!isEditable()}
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>

                                        <hr />
                                        <div className="recap-line d-flex justify-content-between mb-4">
                                            <span className="h6">Net à payer</span>
                                            <span className="h5 text-success fw-bold">{formatCurrency(totaux.net_a_payer)}</span>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                size="lg"
                                                disabled={saving}
                                                className="rounded-pill py-2"
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Mise à jour...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        {originalStatut === 'envoye' ? 'Mettre à jour le statut' : 'Mettre à jour'}
                                                    </>
                                                )}
                                            </Button>
                                            
                                            {isEditable() && (
                                                <Button 
                                                    variant="danger" 
                                                    size="lg"
                                                    onClick={() => setShowDeleteModal(true)}
                                                    className="rounded-pill py-2"
                                                >
                                                    <i className="ri-delete-bin-line me-2"></i>
                                                    Supprimer
                                                </Button>
                                            )}
                                            
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/devisListe')}
                                                className="rounded-pill py-2"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="product-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{ background: '#21ACCD' }}>
                    <Modal.Title className="text-white">
                        <i className="ri-shopping-cart-line me-2"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                   
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits.map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                        <td className="text-center">
    <span className={produit.stock_actuel < produit.seuil_alerte ? 'text-danger fw-bold' : 'text-muted'}>
        {produit.stock_actuel || 0}
    </span>
    {produit.stock_actuel < produit.seuil_alerte && produit.stock_actuel > 0 && (
        <i className="ri-alert-line text-warning ms-1" title="Stock bas"></i>
    )}
    {produit.stock_actuel === 0 && (
        <i className="ri-close-circle-line text-danger ms-1" title="Rupture de stock"></i>
    )}
</td>
                                        <td className="pe-4">
                                            <td className="pe-4">
    <Button 
        size="sm" 
        variant="primary"
        onClick={() => ajouterLigne(produit)}
        disabled={produit.stock_actuel <= 0}
        className="rounded-circle"
        style={{ 
            background: produit.stock_actuel <= 0 ? '#94a3b8' : '#21ACCD', 
            border: 'none', 
            width: '32px', 
            height: '32px' 
        }}
    >
        <i className="ri-add-line"></i>
    </Button>
    {produit.stock_actuel <= 0 && (
        <small className="text-danger d-block">Rupture</small>
    )}
</td>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="outline-secondary" onClick={() => setShowProduitModal(false)} className="rounded-pill px-4">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-2">Êtes-vous sûr de vouloir supprimer ce devis ?</p>
                    <p className="fw-bold text-primary">{devis.numero_proforma}</p>
                    <Alert variant="warning" className="mt-3">
                        <i className="ri-alert-line me-2"></i>
                        Cette action est irréversible.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting} className="px-5 rounded-pill">
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Confirmer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .fs-16 {
                    font-size: 1rem;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                
                .fs-48 {
                    font-size: 3rem;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .product-modal .modal-content {
                    border-radius: 24px;
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .product-row {
                    transition: all 0.2s ease;
                }
                
                .product-row:hover {
                    background: #f8fafc;
                }
                
                .recap-line {
                    padding: 0.5rem 0;
                }
            `}</style>
        </>
    );
}